import {
  Box,
  Button,
  ListItem,
  ListItemButton,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { useAuth, useDialog, useSideMenu } from '@/logic/state';

import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { ILocale } from '@/lang/types';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { WalletSidMenuAuthHint } from './wallet-side-menu-auth-hint';
import { checkIsRtl } from '@/lang/utils';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { useUserBalance } from '@/features/wallet/hooks/use-user-balance';
import { UserType, useUserTypes } from '@/logic/hooks/use-user-types';
import { PATHS } from '@/constants/routes';

const ArrowIconSx: SxProps = {
  transition: '0.3s ease-out',
  '&:hover': {
    transition: '0.3s ease-out',
    WebkitTransform: 'translateX(4px)',
    MozTransform: 'translateX(4px)',
    msTransform: 'translateX(4px)',
    OTransform: 'translateX(4px)',
    transform: 'translateX(4px)',
    WebkitTransition: 'transform 0.3s ease-out',
    MozTransition: 'transform 0.3s ease-out',
    msTransition: 'transform 0.3s ease-out',
    OTransition: 'transform 0.3s ease-out',
  },
};

interface MyBalanceBadgeProps {
  customArrowButtonSx?: SxProps;
}

export const MyBalanceBadge: FC<MyBalanceBadgeProps> = ({ customArrowButtonSx }) => {
  const theme = useTheme();
  const user = useAuth(s => s.user, shallow);
  const setSideMenu = useSideMenu(s => s.setSideMenu, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);
  const router = useRouter();
  const { userType } = useUserTypes();

  const isRtl = checkIsRtl(router.locale as ILocale);

  const { userFormattedBalance } = useUserBalance();

  const handleBalanceBadgeClick = () => {
    setSideMenu(false);
    if (!user) {
      setDialog(() => ({
        wrapper: true,
        isPhoneNumberForm: true,
      }));
      return;
    }
    if (
      userType === UserType.Completed ||
      userType === UserType.Verified ||
      userType === UserType.NonVerified
    ) {
      router.push(PATHS.USER_AREA);
      return;
    }
    if (userType === UserType.NonCompleted) {
      setDialog(() => ({
        wrapper: true,
        isCompleteProfileForm: true,
        completeProfileFormPayload: {
          hideMaybeLaterButton: true,
        },
      }));
      return;
    }
  };

  return (
    <ListItem
      key='e-wallet-badge'
      component='div'
      disableGutters
      sx={{
        paddingY: '10.5px',
        display: {
          mobile: 'flex',
          mediumDesktop: 'flex',
        },
      }}
    >
      <ListItemButton
        component='a'
        disableGutters
        sx={{
          borderRadius: '12px',
          paddingX: '16px',
          paddingY: '8px',
          backgroundColor: theme.palette.lightGrey.main,
        }}
        onClick={handleBalanceBadgeClick}
      >
        <Stack
          direction='row'
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          {user ? (
            userType === UserType.Completed ||
            userType === UserType.Verified ||
            userType === UserType.NonVerified ? (
              <Box>
                <Typography
                  sx={{
                    fontSize: pxToRem(16),
                    fontWeight: '400',
                    color: theme.palette.grey[60],
                    marginBottom: '4px',
                  }}
                >
                  <FormattedMessage id='wallet.my.balance' />
                </Typography>
                <Typography
                  component='span'
                  sx={{
                    fontSize: pxToRem(20),
                    fontWeight: '700',
                    color: theme.palette.grey[60],
                  }}
                >
                  {userFormattedBalance}&nbsp;
                  <FormattedMessage id='currency.SAR' />
                </Typography>
              </Box>
            ) : userType === UserType.NonCompleted ? (
              <WalletSidMenuAuthHint
                message='wallet.user_not_completed_profile.side_menu'
                img='/icons/profile/wallet-cashback-profile-icon.svg'
              />
            ) : null
          ) : (
            <WalletSidMenuAuthHint
              message='wallet.user_is_guest'
              img='/icons/profile/wallet-cashback-login-icon.svg'
            />
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/*
              Decorative chevron, not a control — the row's ListItemButton owns
              the click. Same fix as wallet-cashback-info-badge.tsx.
            */}
            <Button
              variant='contained'
              aria-hidden
              tabIndex={-1}
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '100%',
                minWidth: 'unset',
                minHeight: 'unset',
                ...customArrowButtonSx,
              }}
            >
              {isRtl ? (
                <KeyboardArrowLeftIcon sx={ArrowIconSx} />
              ) : (
                <KeyboardArrowRightIcon sx={ArrowIconSx} />
              )}
            </Button>
          </Box>
        </Stack>
      </ListItemButton>
    </ListItem>
  );
};

export default MyBalanceBadge;
